import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';
import { invitationUnion } from './invitation';
import { profileSchema } from './shared';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';

export enum ProfileMessageType {
  PROFILE_GET = 'PROFILE_GET',
  PROFILE_GET_RESPONSE = 'PROFILE_GET_RESPONSE',
  PROFILE_GET_ERROR = 'PROFILE_GET_ERROR',
  PROFILE_UPDATE = 'PROFILE_UPDATE',
  PROFILE_GET_ALIASES = 'PROFILE_GET_ALIASES',
  PROFILE_GET_ALIASES_RESPONSE = 'PROFILE_GET_ALIASES_RESPONSE',
  PROFILE_GET_ALIASES_ERROR = 'PROFILE_GET_ALIASES_ERROR',
  PROFILE_UPDATE_SUCCESS = 'PROFILE_UPDATE_SUCCESS',
  PROFILE_UPDATE_ERROR = 'PROFILE_UPDATE_ERROR',
  PROFILE_GET_INVITATIONS = 'PROFILE_GET_INVITATIONS',
  PROFILE_GET_INVITATIONS_RESPONSE = 'PROFILE_GET_INVITATIONS_RESPONSE',
  PROFILE_GET_INVITATIONS_ERROR = 'PROFILE_GET_INVITATIONS_ERROR',
}

// Common schemas
export const metaAliasSchema = z.object({
  default: z.string(),
  discord: z.string().optional(),
  twitch: z.string().optional(),
  clerk: z.string().optional(),
});

export const aliasInfoSchema = z.object({
  name: z.string(),
  platform: z.nativeEnum(IdentityPlatform).optional(),
});

export const aliasEntrySchema = z.object({
  authId: z.string(),
  info: aliasInfoSchema,
  expiresAt: z.number().optional(),
  metaAlias: metaAliasSchema.optional(),
});

// Payload schemas
const profileGetPayloadSchema = z.object({
  alias: z.string().optional(),
  platform: z.nativeEnum(IdentityPlatform).optional(),
});

const profileGetResponsePayloadSchema = z.object({
  success: z.boolean(),
  profile: profileSchema.optional(),
  isOwner: z.boolean().optional(),
});

const profileGetErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const profileGetAliasesPayloadSchema = z.object({});

const profileGetAliasesResponsePayloadSchema = z.object({
  success: z.boolean(),
  aliases: metaAliasSchema,
  usernames: z.record(z.nativeEnum(IdentityPlatform), z.string()),
});

const profileGetAliasesErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const profileUpdatePayloadSchema = z.object({
  profile: profileSchema.partial(),
});

const profileUpdateSuccessPayloadSchema = z.object({
  success: z.boolean(),
  authId: z.string(),
  profile: profileSchema,
  isOwner: z.boolean().optional(),
});

const profileUpdateErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

// Payload schemas for invitations
const profileGetInvitationsPayloadSchema = z.object({
  alias: z.string().optional(),
  platform: z.nativeEnum(IdentityPlatform).optional(),
  limit: z.number().min(1).max(50).default(16),
  cursor: z.string().optional(),
  search: z.string().optional(),
  invitationType: z.literal('cafe').optional(),
});

const profileGetInvitationsResponsePayloadSchema = z.object({
  success: z.boolean(),
  invitations: z.array(invitationUnion),
  nextCursor: z.string().optional(),
  totalCount: z.number(),
});

const profileGetInvitationsErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

// Full message schemas
const profileGetSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_GET),
  })
  .merge(profileGetPayloadSchema);

const profileGetResponseSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_GET_RESPONSE),
  })
  .merge(profileGetResponsePayloadSchema);

const profileGetErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_GET_ERROR),
  })
  .merge(profileGetErrorPayloadSchema);

const profileGetAliasesSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_GET_ALIASES),
  })
  .merge(profileGetAliasesPayloadSchema);

const profileGetAliasesResponseSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_GET_ALIASES_RESPONSE),
  })
  .merge(profileGetAliasesResponsePayloadSchema);

const profileGetAliasesErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_GET_ALIASES_ERROR),
  })
  .merge(profileGetAliasesErrorPayloadSchema);

const profileUpdateSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_UPDATE),
  })
  .merge(profileUpdatePayloadSchema);

const profileUpdateSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_UPDATE_SUCCESS),
  })
  .merge(profileUpdateSuccessPayloadSchema);

const profileUpdateErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_UPDATE_ERROR),
  })
  .merge(profileUpdateErrorPayloadSchema);

const profileGetInvitationsSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_GET_INVITATIONS),
  })
  .merge(profileGetInvitationsPayloadSchema);

const profileGetInvitationsResponseSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_GET_INVITATIONS_RESPONSE),
  })
  .merge(profileGetInvitationsResponsePayloadSchema);

const profileGetInvitationsErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(ProfileMessageType.PROFILE_GET_INVITATIONS_ERROR),
  })
  .merge(profileGetInvitationsErrorPayloadSchema);

export const profileMessageSchema = z.discriminatedUnion('type', [
  profileGetSchema,
  profileGetResponseSchema,
  profileGetErrorSchema,
  profileGetAliasesSchema,
  profileGetAliasesResponseSchema,
  profileGetAliasesErrorSchema,
  profileUpdateSchema,
  profileUpdateSuccessSchema,
  profileUpdateErrorSchema,
  profileGetInvitationsSchema,
  profileGetInvitationsResponseSchema,
  profileGetInvitationsErrorSchema,
]);

// Export all payload types
export type ProfileGetPayload = z.infer<typeof profileGetPayloadSchema>;
export type ProfileGetResponsePayload = z.infer<typeof profileGetResponsePayloadSchema>;
export type ProfileGetErrorPayload = z.infer<typeof profileGetErrorPayloadSchema>;
export type ProfileGetAliasesPayload = z.infer<typeof profileGetAliasesPayloadSchema>;
export type ProfileGetAliasesResponsePayload = z.infer<typeof profileGetAliasesResponsePayloadSchema>;
export type ProfileGetAliasesErrorPayload = z.infer<typeof profileGetAliasesErrorPayloadSchema>;
export type ProfileUpdatePayload = z.infer<typeof profileUpdatePayloadSchema>;
export type ProfileUpdateSuccessPayload = z.infer<typeof profileUpdateSuccessPayloadSchema>;
export type ProfileUpdateErrorPayload = z.infer<typeof profileUpdateErrorPayloadSchema>;
export type MetaAlias = z.infer<typeof metaAliasSchema>;
export type AliasInfo = z.infer<typeof aliasInfoSchema>;
export type AliasEntry = z.infer<typeof aliasEntrySchema>;
export type ProfileGetInvitationsPayload = z.infer<typeof profileGetInvitationsPayloadSchema>;
export type ProfileGetInvitationsResponsePayload = z.infer<typeof profileGetInvitationsResponsePayloadSchema>;
export type ProfileGetInvitationsErrorPayload = z.infer<typeof profileGetInvitationsErrorPayloadSchema>;

// Full message type
export type ProfileMessage = z.infer<typeof profileMessageSchema>;

type PayloadType<T extends ProfileMessageType> = T extends ProfileMessageType.PROFILE_GET
  ? ProfileGetPayload
  : T extends ProfileMessageType.PROFILE_GET_RESPONSE
    ? ProfileGetResponsePayload
    : T extends ProfileMessageType.PROFILE_UPDATE
      ? ProfileUpdatePayload
      : T extends ProfileMessageType.PROFILE_UPDATE_SUCCESS
        ? ProfileUpdateSuccessPayload
        : T extends ProfileMessageType.PROFILE_UPDATE_ERROR
          ? ProfileUpdateErrorPayload
          : T extends ProfileMessageType.PROFILE_GET_ALIASES
            ? ProfileGetAliasesPayload
            : T extends ProfileMessageType.PROFILE_GET_ALIASES_RESPONSE
              ? ProfileGetAliasesResponsePayload
              : T extends ProfileMessageType.PROFILE_GET_ALIASES_ERROR
                ? ProfileGetAliasesErrorPayload
                : T extends ProfileMessageType.PROFILE_GET_ERROR
                  ? ProfileGetErrorPayload
                  : T extends ProfileMessageType.PROFILE_GET_INVITATIONS
                    ? ProfileGetInvitationsPayload
                    : T extends ProfileMessageType.PROFILE_GET_INVITATIONS_RESPONSE
                      ? ProfileGetInvitationsResponsePayload
                      : T extends ProfileMessageType.PROFILE_GET_INVITATIONS_ERROR
                        ? ProfileGetInvitationsErrorPayload
                        : never;

export function generateProfileMessage<T extends ProfileMessageType>(type: T, payload: PayloadType<T>): ProfileMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case ProfileMessageType.PROFILE_GET:
      return profileGetSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_GET_RESPONSE:
      return profileGetResponseSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_GET_ERROR:
      return profileGetErrorSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_GET_ALIASES:
      return profileGetAliasesSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_GET_ALIASES_RESPONSE:
      return profileGetAliasesResponseSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_GET_ALIASES_ERROR:
      return profileGetAliasesErrorSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_UPDATE:
      return profileUpdateSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_UPDATE_SUCCESS:
      return profileUpdateSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_UPDATE_ERROR:
      return profileUpdateErrorSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_GET_INVITATIONS:
      return profileGetInvitationsSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_GET_INVITATIONS_RESPONSE:
      return profileGetInvitationsResponseSchema.parse({
        ...base,
        ...payload,
      });
    case ProfileMessageType.PROFILE_GET_INVITATIONS_ERROR:
      return profileGetInvitationsErrorSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}

